import sanitizeHTML from 'sanitize-html';

/**
 * Extracts the query params from the given URL string and return an key-value representation of them.
 *
 * @example
 * Given the following URL: 'www.testURL.com/test?a=true&b=3',
 * getUrlQueryParams('www.testURL.com/test?a=true&b=3') // { a: true, b: 3 }
 *
 * @param url - string
 */
export function getUrlQueryParams (url) {
    const path = decodeURIComponent(url);
    const allQuery = path.split('?')[1];
    if (allQuery) {
        const tuplesList = allQuery.split('&');

        return tuplesList.reduce((acc, tuple) => {
            const [key, value] = tuple.split('=');

            if (key === 'version' || key === 'locale') {
                return acc;
            }

            acc[key] = JSON.parse(value);

            return acc;
        }, {});
    }
}

/**
 * Validates that the given URL is an acceptable Pendo URL
 * expects the  url scheme to be https://
 * @example
 * Given the following URL: 'https://www.testURL.com/test?a=true',
 * isValidUrl('https://www.testURL.com/test?a=true&b=3');
 * // true
 *
 * @param url - string
 */
export function isValidHttpsUrl (url) {
    const testRegex = new RegExp(
        /^(https):\/\/([a-z0-9]+(-+[a-z0-9]+)*\.)+([a-z0-9]+(-+[a-z0-9]+)*\.)+[a-z]{2,}$/,
        'im',
    );

    return testRegex.test(url);
}

/**
 * Validates that the given domain is acceptable for Pendo
 * Valid domain must contain only letters, numbers, and hyphens, and must have at least 1 period before a tld.
 * Valid domain may have any number of sequential hyphens, but may not start or end with a hyphen.
 * IP addresses and ports are not supported. Wildcards are not supported.
 * @example
 * Given the following domain: 'pendo-dev.com',
 * isValidDomain('pendo-dev.com');
 * // true
 *
 * @param domain - string
 */
export function isValidDomain (domain) {
    if (domain === '') {
        return true;
    }

    const validDomainRegex = new RegExp('^([a-z0-9]+(-+[a-z0-9]+)*\\.)+[a-z]{2,}$', 'gm');

    return validDomainRegex.test(domain);
}

export function getSanitizedUrlParam (param) {
    const urlObject = new URL(window.location);

    return sanitizeHTML(urlObject.searchParams.get(param) || '', {
        allowedTags: [],
        allowedAttributes: {},
    });
}

export function getValidatedUrl (url) {
    let validatedUrl = url?.trim();

    if (!validatedUrl) return null;

    if (!validatedUrl.startsWith('http://') && !validatedUrl.startsWith('https://')) {
        validatedUrl = `http://${validatedUrl}`;
    }

    try {
        validatedUrl = new URL(validatedUrl);
    } catch {
        return null;
    }

    if (!isValidDomain(validatedUrl.host)) return null;

    return validatedUrl;
}
